import { useForm } from 'react-hook-form';
import { useCallback, useEffect, useState, useRef } from 'react';
import { TabContext, TabList } from '@mui/lab';
import { format } from 'date-fns';
import { DateBoundTimePicker } from 'components/TimePicker/DateBoundTimePicker';
import { coercePickerDate } from 'utils/timePickerBridge';
import {
  Box,
  Button,
  ButtonGroup,
  Tab,
  Typography,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import { INames } from 'apis/main/type';
import {
  getNandaDomain,
  createNursingRecord,
  updateNursingRecord,
  getOMAHADomain,
  getOMAHAModifiers1,
  getOMAHAModifiers2,
  getOMAHAPriority,
} from 'apis/main';
import useUser from 'store/user/useUser';
import useStudent from 'store/student/useStudent';
import useSurvey from 'store/survey/useSurvey';
import usePatient from 'store/patient/usePatient';
import { requiredSelect } from 'components/Form/requiredItems';
import useNotification from 'hooks/useNotification';
import { findKeyValue } from 'utils/convert';

import { RECORD_TYPE } from '../../Survey/type';
import { initialNursingRecord } from '../../Survey/initialStates';

import Nanda from './Nanda';
import Soapie from './Soapie';
import Remarks from './Remarks';
import FocusDar from './FocusDar';
import NarrativeRecord, { NarrativeRecordHandle } from './NarrativeRecord';
import { StyledTabPanel } from '../../style';
import OMAHA from './OMAHA';

type CodebookCache = {
  // NANDA는 version이 있어서 별도 캐시
  nandaByVersion?: Record<number, INames[]>;
  // OMAHA는 고정이라 1회 로드 캐시
  omahaDomain?: INames[];
  omahaM1?: INames[];
  omahaM2?: INames[];
  omahaPriority?: INames[];
  loadedOmaha: boolean;
};

const codebookCache: CodebookCache = {
  nandaByVersion: {},
  loadedOmaha: false,
};

const NANDA_VERSION_KEY = 'NANDA_VERSION';
const DEFAULT_NANDA_VERSION = 13;

interface Props {
  coachRef: any;
  menuDrawerWidth: number;
  setMenuDrawerWidth: (menuDrawerWidth: number) => void;
}

const NursingRecords = ({ coachRef }: Props) => {
  const { palette } = useTheme();
  const { isStudent } = useUser();
  const { student_uuid: user_id } = useStudent();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { nursingRecord, patientInfo, onClearNursingRecord } = usePatient();
  const { onSuccess, onFail, onRequired } = useNotification();
  const { register, watch, setValue, handleSubmit, reset, getValues } =
    useForm();

  const [recordType, setRecordType] = useState<string>(RECORD_TYPE.NANDA);
  const [recordTime, setRecordTime] = useState<Date | null>(new Date());

  const [isTestMode, setIsTestMode] = useState<boolean>(false);

  const [nandaVersion, setNandaVersion] = useState<number>(() => {
    const saved = sessionStorage.getItem(NANDA_VERSION_KEY);
    const parsed = saved ? Number(saved) : DEFAULT_NANDA_VERSION;
    return Number.isFinite(parsed) ? parsed : DEFAULT_NANDA_VERSION;
  });

  useEffect(() => {
    sessionStorage.setItem(NANDA_VERSION_KEY, String(nandaVersion));
  }, [nandaVersion]);

  // ===== 코드북 상태 =====
  const [domainNames, setDomainNames] = useState<INames[]>([]);
  const [omahaDomainNames, setOmahaDomainNames] = useState<INames[]>([]);
  const [omahaModigiers1Names, setOmahaModifiers1Names] = useState<INames[]>(
    []
  );
  const [omahaModigiers2Names, setOmahaModifiers2Names] = useState<INames[]>(
    []
  );
  const [omahaPriorityNames, setOmahaPriorityNames] = useState<INames[]>([]);

  // NarrativeRecord ref
  const narrativeRecordRef = useRef<NarrativeRecordHandle>(null);

  // ===== NANDA 코드북: version 의존 로드 + version별 캐시 =====
  useEffect(() => {
    let ignore = false;

    const cached = codebookCache.nandaByVersion?.[nandaVersion];
    if (cached && cached.length) {
      setDomainNames(cached);
      return;
    }

    (async () => {
      try {
        const res = await getNandaDomain({ version: nandaVersion });
        if (ignore) return;

        const names = res.data.names ?? [];
        setDomainNames(names);

        if (!codebookCache.nandaByVersion) codebookCache.nandaByVersion = {};
        codebookCache.nandaByVersion[nandaVersion] = names;
      } catch {
        // 필요시 에러 토스트 처리 가능
      }
    })();

    return () => {
      ignore = true;
    };
  }, [nandaVersion]);

  // ===== OMAHA 코드북: 마운트 1회 로드 + 모듈 캐시 =====
  useEffect(() => {
    let ignore = false;

    const applyFromCache = () => {
      if (!codebookCache.loadedOmaha) return false;
      setOmahaDomainNames(codebookCache.omahaDomain ?? []);
      setOmahaModifiers1Names(codebookCache.omahaM1 ?? []);
      setOmahaModifiers2Names(codebookCache.omahaM2 ?? []);
      setOmahaPriorityNames(codebookCache.omahaPriority ?? []);
      return true;
    };

    if (applyFromCache()) return;

    (async () => {
      try {
        const [omahaDomainRes, omahaM1Res, omahaM2Res, omahaPrioRes] =
          await Promise.all([
            getOMAHADomain(),
            getOMAHAModifiers1(),
            getOMAHAModifiers2(),
            getOMAHAPriority(),
          ]);
        if (ignore) return;

        const omahaDomain = omahaDomainRes.data.names ?? [];
        const omahaM1 = omahaM1Res.data.names ?? [];
        const omahaM2 = omahaM2Res.data.names ?? [];
        const omahaPriority = omahaPrioRes.data.names ?? [];

        setOmahaDomainNames(omahaDomain);
        setOmahaModifiers1Names(omahaM1);
        setOmahaModifiers2Names(omahaM2);
        setOmahaPriorityNames(omahaPriority);

        codebookCache.omahaDomain = omahaDomain;
        codebookCache.omahaM1 = omahaM1;
        codebookCache.omahaM2 = omahaM2;
        codebookCache.omahaPriority = omahaPriority;
        codebookCache.loadedOmaha = true;
      } catch {
        // 필요시 에러 토스트 등 처리 가능
      }
    })();

    return () => {
      ignore = true;
    };
  }, []);

  const onRestNursingRecord = useCallback(() => {
    setRecordTime(new Date());

    setValue('collectingData', '');
    setValue('narrativeNote', '');

    setValue('subjective', '');
    setValue('objective', '');
    setValue('planning', '');

    setValue('focus', '');
    setValue('data', '');
    setValue('action', '');
    setValue('response', '');

    reset(initialNursingRecord.nanda);
    reset(initialNursingRecord.soapie);
    reset(initialNursingRecord.remarks);

    onClearNursingRecord();
  }, [reset, onClearNursingRecord, setValue]);

  useEffect(() => {
    if (!nursingRecord) return onRestNursingRecord();

    const { record_time, record_type, content } = nursingRecord;
    const parsed = JSON.parse(content);

    reset(parsed);
    setRecordType(`${record_type}`);
    setRecordTime(new Date(record_time));

    if (`${record_type}` === RECORD_TYPE.NANDA && parsed?.version) {
      setNandaVersion(Number(parsed.version));
    }

    if (`${record_type}` === RECORD_TYPE.NARRATIVE_RECORD) {
      const narrativeHtml = parsed?.narrativeNote ?? '';
      narrativeRecordRef.current?.reloadEditor(narrativeHtml);
      requestAnimationFrame(() => {
        narrativeRecordRef.current?.reloadEditor(narrativeHtml);
      });
    }
  }, [nursingRecord, reset, onRestNursingRecord]);

  const onSubmit = (data: any) => {
    if (!recordTime) return onRequired('REQUIRED.RECORD.TIME');

    let contentKeys: string[] = [];
    switch (recordType) {
      case RECORD_TYPE.NANDA:
        contentKeys = Object.keys(initialNursingRecord.nanda);
        break;
      case RECORD_TYPE.SOAPIE:
        contentKeys = Object.keys(initialNursingRecord.soapie);
        break;
      case RECORD_TYPE.FOCUS_DAR:
        contentKeys = Object.keys(initialNursingRecord.focusDar);
        break;
      case RECORD_TYPE.NARRATIVE_RECORD:
        contentKeys = Object.keys(initialNursingRecord.narrativeRecord);
        break;
      case RECORD_TYPE.REMARKS:
        contentKeys = Object.keys(initialNursingRecord.remarks);
        break;
      case RECORD_TYPE.REMARKSTWO:
        contentKeys = Object.keys(initialNursingRecord.remarks_two);
        break;
      case RECORD_TYPE.OMAHA:
        contentKeys = Object.keys(initialNursingRecord.omaha);
        break;
      case RECORD_TYPE.ICNP:
        contentKeys = Object.keys(initialNursingRecord.icnp);
        break;
      case RECORD_TYPE.ICNPBROWSER:
        contentKeys = Object.keys(initialNursingRecord.icnp_browser);
        break;
      default:
        contentKeys = [];
        break;
    }

    const content: any = findKeyValue(data, contentKeys);

    // ===== NANDA 저장 시 version 포함 + 필수 검증 (첫 번째 코드 적용) =====
    if (recordType === RECORD_TYPE.NANDA) {
      content.version = nandaVersion;

      if (!requiredSelect(content?.domain))
        return onRequired('REQUIRED.NONCHOICE');
      if (!requiredSelect(content?.class))
        return onRequired('REQUIRED.NONCHOICE');
      if (!requiredSelect(content?.diagnosis))
        return onRequired('REQUIRED.NONCHOICE');
    }

    const request = {
      recordType: Number(recordType),
      record_time: format(recordTime, 'yyyy-MM-dd HH:mm'),
      content: JSON.stringify(content),
    };

    if (nursingRecord) {
      return updateNursingRecord({ ...nursingRecord, ...request })
        .then(() => {
          onRestNursingRecord();
          if (recordType === RECORD_TYPE.NARRATIVE_RECORD) {
            narrativeRecordRef.current?.clearDraft();
          }
          navigate(`${pathname}?isUpdateNursingRecord=true`);
          onSuccess('간호기록을 수정하였습니다.');
        })
        .catch(e => onFail('간호기록 수정 실패하였습니다', e));
    }

    createNursingRecord({
      userId: user_id,
      patient_id: patientInfo!.patient_id,
      ...request,
    })
      .then(() => {
        onRestNursingRecord();
        if (recordType === RECORD_TYPE.NARRATIVE_RECORD) {
          narrativeRecordRef.current?.clearDraft();
        }
        navigate(`${pathname}?isUpdateNursingRecord=true`);
        onSuccess('간호기록을 저장하였습니다.');
      })
      .catch(e => onFail('간호기록 저장 실패하였습니다', e));
  };

  const linkHandler = (_value: any) => {
    window.open('https://www.icn.ch/icnp-browser');
    setRecordType('0');
  };

  useEffect(() => {
    if (recordType === '8' || recordType === '5') {
      setRecordType('0');
    }
  }, [recordType]);

  const { onCloseReadOnly, onUpdateSurveyType } = useSurvey();

  const onClickListItem = (label: string) => {
    if (!patientInfo) return onRequired('REQUIRED.PATIENT');
    onUpdateSurveyType(label);
  };

  const nurseRecordModalHandler = (label: string) => {
    onClickListItem(label);
  };

  const mode = sessionStorage.getItem('MODE');
  useEffect(() => {
    if (mode === 'TEST') setIsTestMode(true);
    else setIsTestMode(false);
  }, [mode]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 2.5, mb: 1.5 }}
      >
        <Typography variant="subtitle2" fontSize={13}>
          간호 기록 작성
        </Typography>

        <Box sx={{ width: 130 }}>
          <DateBoundTimePicker
            value={coercePickerDate(recordTime) || null}
            onChange={setRecordTime}
            disabled={!isStudent}
            placeholder="00:00"
          />
        </Box>
      </Box>

      <TabContext value={recordType}>
        <TabList
          variant="fullWidth"
          onChange={(_, value) => {
            setRecordType(value);
          }}
          ref={coachRef}
        >
          <Tab label="NANDA" value={RECORD_TYPE.NANDA} />
          <Tab label="SOAPIE" value={RECORD_TYPE.SOAPIE} />
          <Tab label="Focus DAR" value={RECORD_TYPE.FOCUS_DAR} />
        </TabList>

        <TabList
          variant="fullWidth"
          onChange={(_, value) => {
            if (value === '5') {
              nurseRecordModalHandler('간호과정기록지2');
            }
            setRecordType(value);
          }}
          ref={coachRef}
        >
          <Tab
            label="서술기록(음성AI)"
            value={RECORD_TYPE.NARRATIVE_RECORD}
          />
          <Tab label="간호과정 1" value={RECORD_TYPE.REMARKS} />
          <Tab label="간호과정 2" value={RECORD_TYPE.REMARKSTWO} />
        </TabList>

        <TabList
          variant="fullWidth"
          onChange={(_, value) => {
            if (value === '8') {
              linkHandler(value);
            }
            setRecordType(value);
          }}
          ref={coachRef}
        >
          <Tab label="OMAHA" value={RECORD_TYPE.OMAHA} />
          <Tab label="ICNP" value={RECORD_TYPE.ICNP} disabled />
          <Tab label="ICNP Browser" value={RECORD_TYPE.ICNPBROWSER} />
        </TabList>

        <StyledTabPanel value={RECORD_TYPE.NANDA}>
          <Nanda
            watch={watch}
            setValue={setValue}
            register={register}
            disabled={!isStudent}
            domainNames={domainNames}
            isTestMode={isTestMode}
            // ===== NANDA 버전 props (첫 번째 코드 적용) =====
            nandaVersion={nandaVersion}
            setNandaVersion={setNandaVersion}
          />
        </StyledTabPanel>

        <StyledTabPanel value={RECORD_TYPE.SOAPIE}>
          <Soapie
            register={register}
            disabled={!isStudent}
            isTestMode={isTestMode}
          />
        </StyledTabPanel>

        <StyledTabPanel value={RECORD_TYPE.FOCUS_DAR}>
          <FocusDar
            register={register}
            disabled={!isStudent}
            isTestMode={isTestMode}
          />
        </StyledTabPanel>

        <StyledTabPanel value={RECORD_TYPE.NARRATIVE_RECORD}>
          <NarrativeRecord
            ref={narrativeRecordRef}
            register={register}
            setValue={setValue}
            watch={watch}
            isTestMode={isTestMode}
            editorResetKey={nursingRecord?.nursing_record_id ?? 'new'}
          />
        </StyledTabPanel>

        <StyledTabPanel value={RECORD_TYPE.REMARKS}>
          <Remarks
            register={register}
            disabled={!isStudent}
            isTestMode={isTestMode}
          />
        </StyledTabPanel>

        <StyledTabPanel value={RECORD_TYPE.OMAHA}>
          <OMAHA
            watch={watch}
            setValue={setValue}
            register={register}
            disabled={!isStudent}
            getValues={getValues}
            omahaDomainNames={omahaDomainNames}
            omahaModigiers1Names={omahaModigiers1Names}
            omahaModigiers2Names={omahaModigiers2Names}
            omahaPriorityNames={omahaPriorityNames}
            isTestMode={isTestMode}
          />
        </StyledTabPanel>

        <ButtonGroup
          size="small"
          color="info"
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: '130px',
            gap: '12px',
          }}
        >
          <Button
            sx={{
              color: `${
                palette.mode === 'dark' ? 'lightgrey' : palette.text.primary
              }`,
              backgroundColor: '#fff',
            }}
            variant="text"
            color="inherit"
            disabled={!isStudent}
            onClick={onRestNursingRecord}
          >
            취소
          </Button>

          <Button
            sx={{
              color: `${
                palette.mode === 'dark' ? 'white' : palette.primary.main
              }`,
              backgroundColor: '#fff',
            }}
            variant="text"
            type="submit"
            disabled={!patientInfo || !isStudent}
          >
            저장/서명
          </Button>
        </ButtonGroup>
      </TabContext>
    </form>
  );
};

export default NursingRecords;
